// components/WbObjectSearch.jsx
// Pencarian objek custom (Z*/Y*) yang dipakai BERSAMA oleh semua tab di ABAP
// Source Viewer: Program / Report, Function Module, ABAP Dictionary, Class.
//
// SATU sumber data: katalog hasil sync Custom Workbench (tabel sap_wb_object,
// endpoint /api/sap/workbench/objects). Tidak ada baca LIVE ke SAP per ketikan
// — sama persis dengan kotak search di tab Custom Workbench, dan sejalan
// dengan prinsip "hasil fetch dipakai bersama semua tab".
//
// Pencariannya LINTAS TIPE dan dikelompokkan per tipe objek. Objek yang bukan
// milik tab yang sedang dibuka tetap muncul; memilihnya memanggil
// onJump(mode, name) sehingga halaman induk pindah tab dan langsung
// membukanya. Tipe yang tidak punya penampil sendiri (FUGR, DOMA, DTEL, TTYP,
// SHLP, TRAN, MSAG) diarahkan ke tab Custom Workbench, bukan disembunyikan:
// katalog tahu objek itu ada, dan menyembunyikannya membuat hasil pencarian
// terasa bohong.

import { useEffect, useState } from 'react';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Tipe objek TADIR -> tab yang bisa MEMBUKA objek itu.
// FUGR sengaja diarahkan ke 'wb': tab Function Module membaca satu FUNCTION
// MODULE, sedangkan katalog TADIR hanya menyimpan FUNCTION GROUP-nya.
export const TYPE_MODE = {
  PROG: 'prog',
  CLAS: 'cls',
  INTF: 'cls',
  TABL: 'dd',
  VIEW: 'dd',
  FUGR: 'wb',
  DOMA: 'wb',
  DTEL: 'wb',
  TTYP: 'wb',
  SHLP: 'wb',
  TRAN: 'wb',
  MSAG: 'wb',
};

export const TYPE_LABEL = {
  PROG: 'Program / Report',
  FUGR: 'Function Group',
  CLAS: 'Class',
  INTF: 'Interface',
  TABL: 'Table',
  DTEL: 'Data Element',
  DOMA: 'Domain',
  TTYP: 'Table Type',
  VIEW: 'View',
  SHLP: 'Search Help',
  TRAN: 'Transaction',
  MSAG: 'Message Class',
};

const MODE_LABEL = {
  prog: 'Program / Report',
  fm: 'Function Module',
  dd: 'ABAP Dictionary',
  cls: 'Class',
  wb: 'Custom Workbench',
};

// Tipe yang PALING relevan untuk tiap tab. Dipakai hanya untuk urutan grup,
// bukan untuk membuang hasil.
const MODE_PREFER = {
  prog: ['PROG'],
  fm: ['FUGR'],
  cls: ['CLAS', 'INTF'],
  dd: ['TABL', 'VIEW'],
  wb: [],
};

const ORDER = ['PROG', 'FUGR', 'CLAS', 'INTF', 'TABL', 'VIEW',
  'DTEL', 'DOMA', 'TTYP', 'SHLP', 'TRAN', 'MSAG'];

// ── Hook pencarian katalog ───────────────────────────────────────────────────
// Debounce 250ms. Query lokal ke SQLite, jadi murah — debounce di sini soal
// jumlah render, bukan soal beban SAP.
export function useWbSearch(api, connId, term) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!connId) { setRows([]); setTotal(0); setErr(null); return undefined; }
    let alive = true;
    const q = String(term || '').trim().toUpperCase();
    const h = setTimeout(() => {
      const p = new URLSearchParams({ connectionId: String(connId), limit: '150' });
      if (q) p.set('q', q);
      api('/api/sap/workbench/objects?' + p.toString())
        .then(d => {
          if (!alive) return;
          setRows((d && d.rows) || []);
          setTotal((d && d.total) || 0);
          setErr(null);
        })
        .catch(e => { if (alive) { setRows([]); setTotal(0); setErr(e.message); } });
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [api, connId, term]);

  return { rows, total, err };
}

// ── Daftar hasil (dropdown di bawah input tiap tab) ──────────────────────────
export default function WbObjectSearch({ rows, total, err, mode, onPick, onJump }) {
  const prefer = MODE_PREFER[mode] || [];
  const byType = {};
  (rows || []).forEach(r => {
    const tp = r.object || '?';
    (byType[tp] = byType[tp] || []).push(r);
  });

  const seen = {};
  const order = [];
  prefer.concat(ORDER).forEach(tp => {
    if (!seen[tp]) { seen[tp] = 1; order.push(tp); }
  });
  Object.keys(byType).forEach(tp => {
    if (!seen[tp]) { seen[tp] = 1; order.push(tp); }
  });

  const groups = [];
  order.forEach(tp => { if (byType[tp]) groups.push([tp, byType[tp]]); });

  const shown = (rows || []).length;

  return (
    <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 4,
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 320,
      overflowY: 'auto', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' }}>

      {err && (
        <div style={{ padding: '0.45rem 0.65rem', fontSize: 11.5, color: '#b91c1c' }}>{err}</div>
      )}

      {!err && shown === 0 && (
        <div style={{ padding: '0.5rem 0.65rem', fontSize: 11.5, color: '#94a3b8' }}>
          Nothing in the Custom Workbench catalog matches. The name may still exist in SAP —
          run Sync TADIR on the Custom Workbench tab if the object is new.
        </div>
      )}

      {groups.map(([tp, list]) => {
        const target = TYPE_MODE[tp] || 'wb';
        const here = target === mode;
        return (
          <div key={tp}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8,
              padding: '0.3rem 0.65rem', background: '#f8fafc',
              borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
              color: here ? '#0f172a' : '#94a3b8' }}>
              <span>{tp} · {TYPE_LABEL[tp] || tp}</span>
              {!here && <span style={{ fontWeight: 600 }}>opens in {MODE_LABEL[target] || target}</span>}
            </div>
            {list.map(r => (
              <button
                key={tp + '/' + r.obj_name}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  if (here) onPick(r.obj_name, r);
                  else onJump(target, r.obj_name, r);
                }}
                style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                  padding: '0.4rem 0.65rem', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 12.5, textAlign: 'left' }}
              >
                <span style={{ fontFamily: MONO, color: '#0f172a' }}>{r.obj_name}</span>
                <span style={{ color: '#94a3b8', flexShrink: 0, fontSize: 11 }}>
                  {r.devclass || '—'}
                  {r.state === 'ok' && r.lines ? ' · ' + r.lines + ' lines cached' : ''}
                </span>
              </button>
            ))}
          </div>
        );
      })}

      <div style={{ padding: '0.4rem 0.65rem', borderTop: '1px solid #f1f5f9',
        fontSize: 11, color: '#94a3b8' }}>
        {total > shown
          ? 'Showing ' + shown + ' of ' + total + ' catalog matches — narrow your input.'
          : shown + ' catalog match' + (shown === 1 ? '' : 'es') + ' · from Custom Workbench, no live SAP read'}
      </div>
    </div>
  );
}
